import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to handle global keyboard shortcuts
 * @param {Object} shortcuts - Object mapping key combinations to handler functions
 */
export const useKeyboardShortcuts = (shortcuts) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if the user is typing in an input, textarea, or other form element
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        return;
      }

      // Create a key identifier (e.g., 'ctrl+n')
      const key = event.key.toLowerCase();
      const ctrlKey = event.ctrlKey || event.metaKey; // metaKey for Mac
      const altKey = event.altKey;
      const shiftKey = event.shiftKey;

      let keyCombo = '';
      if (ctrlKey) keyCombo += 'ctrl+';
      if (altKey) keyCombo += 'alt+';
      if (shiftKey) keyCombo += 'shift+';
      keyCombo += key;

      // Check if this key combination has a handler
      const handler = shortcuts[keyCombo];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Clean up
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [shortcuts]);
};

/**
 * Hook to handle the default Quick Actions keyboard shortcuts
 */
export const useQuickActionShortcuts = () => {
  const navigate = useNavigate();

  const shortcuts = {
    'ctrl+n': () => navigate('/product-catalog', { state: { openAddModal: true } }),
    'ctrl+p': () => navigate('/reports', { state: { openReportBuilder: true } }),
    'ctrl+s': () => navigate('/product-catalog', { 
      state: { 
        openAdjustmentModal: true,
        notification: {
          type: 'info',
          message: 'Please select products to adjust inventory levels'
        }
      } 
    }),
    'ctrl+r': () => navigate('/reports', { 
      state: { 
        openReportBuilder: true,
        reportType: 'inventory'
      } 
    }),
    'ctrl+b': () => navigate('/product-catalog', { state: { openScanModal: true } }),
    'ctrl+i': () => navigate('/reports', { 
      state: { 
        openAuditTool: true,
        notification: {
          type: 'info',
          message: 'Starting inventory audit session'
        }
      } 
    }),
  };

  useKeyboardShortcuts(shortcuts);

  return shortcuts;
};